import {
	DataGrid,
	DataGridProps,
	EmptyTableBody,
	error,
	openDrawer,
	UnitsContext,
	WarningDialog,
} from '@innovyze/stylovyze';
import {
	GridColumnsInitialState,
	GridFilterModel,
	GridLogicOperator,
	GridOverlayProps,
	GridPaginationInitialState,
	GridPinnedColumns,
	GridRowsProp,
	GridSortModel,
	useGridApiRef,
} from '@mui/x-data-grid-premium';
import React, { useContext, useMemo, useState } from 'react';
import { tableColumns } from './TableColumns';
import { PaperStyled } from '@Utils/styles';
import { TableToolbar } from './TableToolbar';
import { rowsPerPageOptions } from '@innovyze/lib-am-common/Reducers';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';
import {
	addRehabDeletedAction,
	deleteRehabActionResolved,
	getGroupResult,
	setDataGridState,
} from '@Actions';
import { GroupAction } from './MenuCell';
import {
	selectCurrentTree,
	selectRehabActions,
} from '@Selectors/Rehab.selectors';
import { selectDataGridState } from '@Selectors/util.selectors';
import { AGSetMode, RehabAction } from '@Types/rehab.types';
import { useRehabResultSelectors } from '@Components/RehabResult/useRehabResultsSelectors';
import { WarningBar } from './WarningBar';
import { Stack } from '@mui/material';
import CanvasIcon from '@Components/Tools/CanvasIcon.svg';
import { MEASUREMENT_SYSTEM } from '@Utils/units/unitsMappingValues';

interface GroupDatagriddParams {
	checkActionExists: (rehabAction: RehabAction) => boolean;
	onRehabActionChange: (rehabAction: RehabAction | null) => void;
	isDirty: boolean;
	isSaveDisabled: boolean;
	isRunDisabled: boolean;
	isRehabTreeValidating: boolean;
	isRehabTreeRunning: boolean;
	showWarning: boolean;
	hasFullAccess: boolean;
	handleSaveTree: () => void;
	handleRunTree: () => void;
}

export const GroupDatagrid = ({
	checkActionExists,
	onRehabActionChange,
	isDirty,
	isSaveDisabled,
	isRunDisabled,
	isRehabTreeValidating,
	isRehabTreeRunning,
	handleSaveTree,
	handleRunTree,
	showWarning,
	hasFullAccess,
}: GroupDatagriddParams) => {
	const { t } = useGlobalization();
	const rehabActions = selectRehabActions();

	const dispatch = useDispatch();
	const apiRef = useGridApiRef();
	const { _id, mode, lastRun } = selectCurrentTree();

	const [deleteAction, setDeleteAction] = useState<RehabAction | undefined>(
		undefined,
	);

	// selectors
	const { tablePage, isLoading } = useRehabResultSelectors(true);

	const rows: GridRowsProp = useMemo(() => {
		// We dont get any details until its run but we also don't want to display if dirty as it could be wrong!
		// Not sure what we should do about it being saved but not run so the information is out of date... hmmmmm
		if (tablePage) {
			// Merge the rehab actions with the counts
			return rehabActions.map(action => {
				const row = tablePage.cells.find(row => row[6] === action._id);
				if (row) {
					return {
						...action,
						assetCount: row[2],
						assetCountPercent: row[3],
						assetLength: row[4],
						assetLengthPercent: row[5],
					};
				} else if (!isLoading && lastRun) {
					return {
						...action,
						assetCount: 0,
						assetCountPercent: 0,
						assetLength: 0,
						assetLengthPercent: 0,
					};
				} else {
					return action;
				}
			});
		} else {
			return rehabActions;
		}
	}, [rehabActions, tablePage, isLoading]);

	const dataGridKey = `${_id}group`;
	const dataGridState = selectDataGridState(dataGridKey);

	React.useEffect(() => {
		if (lastRun) dispatch(getGroupResult());
	}, [_id]);

	const searchString: string = useMemo(() => {
		if (dataGridState?.filter?.filterModel?.quickFilterValues?.length !== 0)
			return (
				dataGridState?.filter?.filterModel?.quickFilterValues?.[0] ?? ''
			);
		else {
			return '';
		}
	}, [dataGridState]);

	const paginationModel = useMemo(() => {
		return {
			page: dataGridState?.pagination?.paginationModel?.page ?? 0,
			pageSize:
				dataGridState?.pagination?.paginationModel?.pageSize ?? 10,
		};
	}, [dataGridState]);

	const initialPaginationModel: GridPaginationInitialState | undefined =
		useMemo(() => {
			if (dataGridState?.pagination) return dataGridState?.pagination;
			else {
				return undefined;
			}
		}, [dataGridState]);

	const sortModel = useMemo(() => {
		return dataGridState?.sorting?.sortModel;
	}, [dataGridState]);

	const initialSortModel: GridSortModel = useMemo(() => {
		if (dataGridState?.sorting?.sortModel)
			return dataGridState?.sorting?.sortModel;
		else
			return [
				{
					field: 'actionId',
					sort: 'asc',
				},
			];
	}, [dataGridState]);

	const filterModel = useMemo(() => {
		let filter = dataGridState?.filter?.filterModel;
		if (filter === undefined)
			filter = {
				items: [],
				logicOperator: GridLogicOperator.Or,
				quickFilterValues: [],
				quickFilterLogicOperator: GridLogicOperator.And,
			};

		if (searchString.length !== 0)
			return { ...filter, quickFilterValues: [searchString] };
		else return { ...filter, quickFilterValues: [] };
	}, [dataGridState, searchString]);

	const initialFilterModel: GridFilterModel | undefined = useMemo(() => {
		if (dataGridState?.filter?.filterModel)
			return dataGridState?.filter?.filterModel;
		else {
			return undefined;
		}
	}, [dataGridState]);

	const initialPinnedColumns: GridPinnedColumns | undefined = useMemo(() => {
		if (dataGridState) return dataGridState?.pinnedColumns;
		else
			return {
				left: ['assetId'],
			};
	}, [dataGridState]);

	const initialColumns: GridColumnsInitialState | undefined = useMemo(() => {
		if (dataGridState) return dataGridState.columns;
		else
			return {
				// Give this a fake column to make it override any existing selection when changing standard on page
				columnVisibilityModel: { _________: false },
			};
	}, [dataGridState]);

	const onActionClicked = (action: GroupAction, groupId: string) => {
		switch (action) {
			case GroupAction.Edit:
				if (rehabActions && onRehabActionChange) {
					const toEdit = rehabActions.find(
						action => action._id == groupId,
					);
					if (toEdit) {
						onRehabActionChange(toEdit);
						dispatch(openDrawer('addNewActionDrawer'));
					}
				}
				break;
			case GroupAction.Delete:
				if (rehabActions) {
					const toDelete = rehabActions.find(
						action => action._id == groupId,
					);
					if (toDelete) {
						setDeleteAction(toDelete);
					}
				}
				break;
		}
	};

	const { system } = useContext(UnitsContext);

	const columns = useMemo(() => {
		const lengthUnit =
			system === MEASUREMENT_SYSTEM.METRIC ? t('Kilometers') : t('Miles');
		return tableColumns(
			t,
			lengthUnit,
			onActionClicked,
			mode === AGSetMode.CUSTOM,
			rehabActions,
		);
	}, [rehabActions]);

	const onPaginationModelChange: DataGridProps['onPaginationModelChange'] =
		React.useCallback(
			paginationModel => {
				let state = apiRef.current.exportState();

				state = {
					...state,
					pagination: {
						...state.pagination,
						paginationModel: paginationModel,
					},
				};

				dispatch(
					setDataGridState({
						key: dataGridKey,
						initialState: state,
					}),
				);
			},
			[dataGridKey],
		);

	const onSortModelChange: DataGridProps['onSortModelChange'] =
		React.useCallback(
			sortModel => {
				let state = apiRef.current.exportState();

				state = {
					...state,
					sorting: {
						...state.sorting,
						sortModel: sortModel,
					},
				};

				dispatch(
					setDataGridState({
						key: dataGridKey,
						initialState: state,
					}),
				);
			},
			[dataGridKey],
		);

	const onFilterModelChange: DataGridProps['onFilterModelChange'] =
		React.useCallback(
			filterModel => {
				let state = apiRef.current.exportState();

				state = {
					...state,
					filter: {
						...state.filter,
						filterModel: filterModel,
					},
				};

				dispatch(
					setDataGridState({
						key: dataGridKey,
						initialState: state,
					}),
				);
			},
			[dataGridKey],
		);

	const rowCount = useMemo(() => {
		return dataGridState?.pagination?.rowCount ?? rehabActions.length;
	}, [dataGridState]);

	const onRowCountChange: DataGridProps['onRowCountChange'] =
		React.useCallback(
			rowCount => {
				let state = apiRef.current.exportState();

				state = {
					...state,
					pagination: {
						...state.pagination,
						rowCount: rowCount,
					},
				};

				dispatch(
					setDataGridState({
						key: dataGridKey,
						initialState: state,
					}),
				);
			},
			[dataGridKey],
		);

	const onStateChange = React.useCallback(() => {
		// We dont want to use the individual states that could be passed but just export the whole state to keep it all in sync
		const state = apiRef.current.exportState();
		dispatch(
			setDataGridState({
				key: dataGridKey,
				initialState: state,
			}),
		);
	}, [dataGridKey]);

	const filterRef = useMemo(() => React.createRef<HTMLButtonElement>(), []);

	const showFilterPanel = () => {
		if (apiRef.current.showFilterPanel) apiRef.current.showFilterPanel();
	};

	const onNewGroup = () => {
		if (onRehabActionChange) onRehabActionChange(null);
		dispatch(openDrawer('addNewActionDrawer'));
	};

	const onSearch = (searchString: string) => {
		let state = apiRef.current.exportState();

		state = {
			...state,
			filter: {
				...state.filter,
				filterModel: {
					...filterModel,
					quickFilterValues:
						searchString.length !== 0 ? [searchString] : undefined,
				},
			},
		};

		dispatch(
			setDataGridState({
				key: dataGridKey,
				initialState: state,
			}),
		);
	};

	return (
		<PaperStyled
			style={{ height: '100%' }}
			className={isLoading ? 'wait' : ''}>
			<WarningDialog
				open={deleteAction !== undefined}
				title={t('Delete Group?')}
				content={t('Are you sure you want to delete this Group?')}
				cancelText={t('Cancel')}
				confirmText={t('Delete')}
				onCancel={() => setDeleteAction(undefined)}
				onConfirm={() => {
					if (deleteAction && !checkActionExists(deleteAction)) {
						dispatch(addRehabDeletedAction(deleteAction._id));

						// Remove it from the actions array
						dispatch(deleteRehabActionResolved(deleteAction._id));
						setDeleteAction(undefined);
					} else {
						dispatch(
							error(
								t(
									'Failed to delete group. Group cannot be deleted if it is being used in tree.',
								),
							),
						);
						setDeleteAction(undefined);
					}
				}}
			/>
			<Stack>
				<WarningBar
					show={!isLoading && showWarning}
					message={t(
						'The asset counts and percentages may be out of date. Re-run the Asset Group Set to update the counts and percentages.',
					)}
				/>
				<DataGrid
					apiRef={apiRef}
					columns={columns}
					rows={rows}
					getRowId={row => row._id}
					skeletonRowCount={paginationModel.pageSize ?? 10}
					pagination
					pageSizeOptions={rowsPerPageOptions()}
					paginationModel={paginationModel}
					onPaginationModelChange={onPaginationModelChange}
					onRowCountChange={onRowCountChange}
					sortModel={sortModel}
					onSortModelChange={onSortModelChange}
					disableMultipleColumnsSorting
					sortingOrder={['desc', 'asc']}
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					slots={{
						toolbar: TableToolbar,
						noResultsOverlay: EmptyTableBody,
					}}
					slotProps={{
						panel: {
							// anchorEl: filterRef.current, // Bug 155409: MUI data grids filter anchoring
						},
						toolbar: {
							searchString: searchString,
							onSearch: onSearch,
							showFilterPanel: showFilterPanel,
							filterRef: filterRef,
							onNewGroup,
							isSaveDisabled:
								isLoading || !isDirty || isSaveDisabled,
							isRunDisabled:
								isLoading || isDirty || isRunDisabled,
							isRehabTreeValidating,
							isRehabTreeRunning,
							onSave: handleSaveTree,
							onRun: handleRunTree,
						},
						noResultsOverlay: {
							title: t('No Groups'),
							subtitle: t(
								'No groups match that search and/or filter',
							),
						} as GridOverlayProps, // To remove the error about subtitle
					}}
					footerHelper={
						!isLoading && paginationModel
							? t('{{from}}-{{to}} of {{total}}', {
									from:
										rowCount !== 0
											? paginationModel.page *
													paginationModel.pageSize +
											  1
											: 0,
									to:
										(paginationModel.page + 1) *
											paginationModel.pageSize >
										rowCount
											? rowCount
											: (paginationModel.page + 1) *
											  paginationModel.pageSize,
									total: rowCount,
							  })
							: undefined
					}
					emptyState={{
						icon: (
							<img
								src={CanvasIcon}
								alt={t('Decision Tree Icon')}
							/>
						),
						title: t('There are no groups defined'),
						subtitle: hasFullAccess
							? t('Create a group you can then add to the tree.')
							: '',
					}}
					initialState={{
						pinnedColumns: initialPinnedColumns,
						columns: initialColumns,
						pagination: initialPaginationModel,
						sorting: {
							sortModel: initialSortModel,
						},
						filter: {
							filterModel: initialFilterModel,
						},
					}}
					// DONT USE onStateChange={onStateChange} AS THIS GETS CALLED FOR EVERY USER ACTION... SO PROBABLY BEST TO AVOID !!
					// These are the other states we want to monitor.. ones that are not explicitly managed though mode params
					onColumnOrderChange={onStateChange}
					onColumnVisibilityModelChange={onStateChange}
					onPinnedColumnsChange={onStateChange}
					onColumnWidthChange={onStateChange}
					dataCy="group-table"
				/>
			</Stack>
		</PaperStyled>
	);
};
